import React, { useEffect, useState } from "react";
import { TabPanel, TabView } from "primereact/tabview";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Button } from "primereact/button";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { DropdownChangeEvent } from "primereact/dropdown";
import { InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputSwitchChangeEvent } from "primereact/inputswitch";
import { T } from "services/lang/LanguageService";
import SparkLineAdvancedInputs from "../sparkline-advanced/SparkLineAdvancedInputs";
import SparkLineInputs from "./SparkLineInputs";
import { LineConfig, SparkLineProps } from "./SparkLineTypes";
import { DatasetLoadingConf } from "../charts/interfaces";
import { DefaultDatasetLoadingConf } from "../charts/LoadConfigInputs";
import SparkLineEditorGeneral from "./SparkLineEditorGeneral";

const defaultSparkLineConfig: SparkLineProps = {
  decimalPlaces: 1,
  labelFontSize: "2.5rem",
  diffNumFontSize: "2.5rem",
  diffArrowFontSize: "1.8rem",
  showValueChangeIndicator: true,
  sparkLineWidth: undefined,
  dataset: null,
  count: 300,
  useLoadConf: true,
  updateFrequency: 60,
  isMultipleCharts: false,
  multipleCharts: [],
  lineColor: "#000000",
  lineWidth: 1,
  data: [],
  loadConf: DefaultDatasetLoadingConf,
};

const defaultAdvancedSparkLineConfig: Partial<SparkLineProps> = {
  lineColor: "#000000",
  baseline: {
    usage: "dataset",
    color: "#000000",
    fillAboveColor: "#9ACD32",
    fillBelowColor: "#9ACD32",
    width: 1,
  },
  minLine: {
    usage: "none",
    color: "#ffe48a",
  },
  maxLine: {
    usage: "none",
    color: "#fd9c91",
  },
  showYAxis: true,
};

const cleanConfig = (config: SparkLineProps): SparkLineProps => {
  const {
    constant,
    baselineDataset,
    baselineColor,
    baselineWidth,
    fillAboveColor,
    fillBelowColor,
    showMinMax,
    showCurrent,
    useConstant,
    ...rest
  } = config as any;

  const line = useConstant ? { constant } : { dataset: baselineDataset };

  return {
    ...rest,
    baseline: {
      ...line,
      usage: useConstant ? "constant" : "dataset",
      color: baselineColor,
      width: baselineWidth,
      fillAboveColor,
      fillBelowColor,
      ...config.baseline,
    },
    minLine: {
      color: defaultAdvancedSparkLineConfig.minLine?.color,
      usage: "none",
      ...config.minLine,
    },
    maxLine: {
      usage: "none",
      color: defaultAdvancedSparkLineConfig.maxLine?.color,
      ...config.maxLine,
    },
    multipleCharts: config.multipleCharts || [],
  };
};

interface SparkLineEditorProps extends DashboardEditorProps<SparkLineProps> {
  advanced?: boolean;
}

const SparkLineEditor = ({ config, onConfigChange, advanced }: SparkLineEditorProps) => {
  const [isAdvancedOptions, setIsAdvancedOptions] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(() => {
    if (Object.keys(config).length === 0) {
      return advanced
        ? {
            ...defaultSparkLineConfig,
            ...defaultAdvancedSparkLineConfig,
            multipleCharts: [],
          }
        : defaultSparkLineConfig;
    }
    return cleanConfig(config);
  });

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig, config.multipleCharts]);

  const updateChart = (index: number, update: React.SetStateAction<SparkLineProps>) => {
    setCurrentConfig(prevConfig => {
      const charts = [...prevConfig.multipleCharts];
      charts[index] =
        typeof update === "function"
          ? (update(charts[index]) as SparkLineProps)
          : { ...charts[index], ...update };

      return {
        ...prevConfig,
        multipleCharts: charts,
      };
    });
  };

  const handleChartInputChange = (
    index: number,
    e:
      | InputNumberValueChangeEvent
      | React.ChangeEvent<HTMLInputElement>
      | InputSwitchChangeEvent
      | DropdownChangeEvent
  ) => {
    const { id, value } = e.target;
    updateChart(index, { [id]: value } as React.SetStateAction<SparkLineProps>);
  };

  const handleChartNumberInputChange = (
    index: number,
    input: string,
    value: LineConfig | number
  ) => {
    updateChart(index, { [input]: value } as unknown as React.SetStateAction<SparkLineProps>);
  };

  const addNewChart = () => {
    setCurrentConfig(prev => ({
      ...prev,
      isMultipleCharts: true,
      multipleCharts: [
        ...prev.multipleCharts,
        {
          ...prev,
          multipleCharts: [],
        },
      ],
    }));
  };

  const removeChart = (index: number) => {
    confirmDialog({
      message: T("confirm-dialog.are-you-sure-you-want-to-remove-this-chart"),
      header: T("confirm-dialog.confirmation"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: T("action.remove"),
      rejectLabel: T("word.no"),
      accept: () => {
        const charts = [...currentConfig.multipleCharts];
        charts.splice(index, 1);
        setCurrentConfig({
          ...currentConfig,
          multipleCharts: charts,
          isMultipleCharts: charts.length > 0,
        });
      },
    });
  };

  const handleLoadConfChange = (
    updatedLoadConf: DatasetLoadingConf | undefined,
    index?: number
  ) => {
    if (index === undefined || index === null) {
      return setCurrentConfig(prev => ({
        ...prev,
        useLoadConf: updatedLoadConf !== undefined,
        loadConf: updatedLoadConf,
      }));
    }
    const multipleCharts = [...currentConfig.multipleCharts];
    multipleCharts[index] = {
      ...multipleCharts[index],
      useLoadConf: updatedLoadConf !== undefined,
      loadConf: updatedLoadConf,
    };
    return setCurrentConfig({
      ...currentConfig,
      multipleCharts,
    });
  };

  const handleOnConfigChange = (
    e:
      | InputSwitchChangeEvent
      | InputNumberValueChangeEvent
      | DropdownChangeEvent
      | React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.id === "showYAxis" && !e.target.value) {
      return setCurrentConfig({
        ...currentConfig,
        minYAxis: undefined,
        maxYAxis: undefined,
        showYAxis: e.target.value,
      });
    }
    return setCurrentConfig({ ...currentConfig, [e.target.id]: e.target.value });
  };

  const handleSetDefaultProperties = (chartConfig: SparkLineProps, index: number) => {
    const maxLine = {
      ...defaultAdvancedSparkLineConfig.maxLine,
      usage: chartConfig.maxLine?.usage,
      dataset: chartConfig.maxLine?.dataset,
    };
    const minLine = {
      ...defaultAdvancedSparkLineConfig.minLine,
      usage: chartConfig.minLine?.usage,
      dataset: chartConfig.minLine?.dataset,
    };
    const baseline = {
      ...defaultAdvancedSparkLineConfig.baseline,
      usage: chartConfig.baseline?.usage,
      dataset: chartConfig.baseline?.dataset,
    };
    const newConfig = {
      ...chartConfig,
      ...defaultSparkLineConfig,
      ...defaultAdvancedSparkLineConfig,
      isMultipleCharts: chartConfig.isMultipleCharts,
      dataset: chartConfig.dataset,
      maxLine,
      minLine,
      baseline,
    };

    setCurrentConfig(prevConfig => {
      if (index === -1) {
        return {
          ...newConfig,
          multipleCharts: prevConfig.multipleCharts,
        };
      }
      const charts = [...prevConfig.multipleCharts];
      charts[index] = newConfig;
      return {
        ...prevConfig,
        multipleCharts: charts,
      };
    });
  };

  return (
    <div className="flex flex-column gap-2">
      <ConfirmDialog />
      {advanced && (
        <div className="flex flex-row justify-content-end">
          <Button
            icon="pi pi-plus"
            label={T("action.add")}
            className="component-editor-button icon-green"
            tooltip={T("action.add-new-chart")}
            tooltipOptions={{ position: "bottom" }}
            onClick={addNewChart}
          />
        </div>
      )}
      {currentConfig.multipleCharts.length > 0 ? (
        <TabView>
          <TabPanel header={T("word.general")}>
            <SparkLineEditorGeneral
              currentConfig={currentConfig}
              setCurrentConfig={setCurrentConfig}
            />
          </TabPanel>
          <TabPanel header={currentConfig.chartTitle || `${T("word.chart")} 1`}>
            <div className="flex flex-wrap gap-2">
              <SparkLineInputs
                currentConfig={currentConfig}
                onDatasetChange={(key, value) =>
                  setCurrentConfig({ ...currentConfig, [key]: value })
                }
                onConfigChange={e => handleOnConfigChange(e)}
                onNumberChange={(input: string, value: number) =>
                  setCurrentConfig({ ...currentConfig, [input]: value })
                }
                handleLoadConfChange={handleLoadConfChange}
                advanced={advanced}
                isAdvancedOptions={isAdvancedOptions}
                setIsAdvancedOptions={setIsAdvancedOptions}
                isMultipleCharts
                handleSetDefaultProperties={() => handleSetDefaultProperties(currentConfig, -1)}
              />
              {advanced && (
                <SparkLineAdvancedInputs
                  currentConfig={currentConfig}
                  onDatasetChange={setCurrentConfig}
                  onLineConfigChange={(input: string, value: LineConfig) =>
                    setCurrentConfig({ ...currentConfig, [input]: value })
                  }
                  isAdvancedOptions={isAdvancedOptions}
                />
              )}
            </div>
          </TabPanel>
          {currentConfig.multipleCharts.map((chart, index) => (
            <TabPanel
              key={chart.id ?? index}
              header={chart.chartTitle || `${T("word.chart")} ${index + 1 + 1}`}
            >
              <div className="flex flex-wrap gap-2">
                <SparkLineInputs
                  currentConfig={chart}
                  onDatasetChange={(key, value) =>
                    updateChart(index, {
                      [key]: value,
                    } as unknown as React.SetStateAction<SparkLineProps>)
                  }
                  onConfigChange={e => handleChartInputChange(index, e)}
                  onNumberChange={(input: string, value: number) =>
                    handleChartNumberInputChange(index, input, value)
                  }
                  handleLoadConfChange={(updatedLoadConf: DatasetLoadingConf | undefined) =>
                    handleLoadConfChange(updatedLoadConf, index)
                  }
                  advanced={advanced}
                  isAdvancedOptions={isAdvancedOptions}
                  setIsAdvancedOptions={setIsAdvancedOptions}
                  isMultipleCharts
                  handleSetDefaultProperties={() => handleSetDefaultProperties(chart, index)}
                />
                {advanced && (
                  <SparkLineAdvancedInputs
                    currentConfig={chart}
                    onDatasetChange={updated => updateChart(index, updated)}
                    onLineConfigChange={(input: string, value: LineConfig) =>
                      handleChartNumberInputChange(index, input, value)
                    }
                    isAdvancedOptions={isAdvancedOptions}
                  />
                )}
                <Button
                  className="component-editor-button icon-red"
                  icon="pi pi-trash"
                  label={T("action.remove-chart")}
                  onClick={() => removeChart(index)}
                />
              </div>
            </TabPanel>
          ))}
        </TabView>
      ) : (
        <div className="flex flex-wrap gap-2">
          <SparkLineInputs
            currentConfig={currentConfig}
            onDatasetChange={(key, value) => setCurrentConfig({ ...currentConfig, [key]: value })}
            onConfigChange={e => handleOnConfigChange(e)}
            onNumberChange={(input: string, value: number) =>
              setCurrentConfig({ ...currentConfig, [input]: value })
            }
            handleLoadConfChange={handleLoadConfChange}
            advanced={advanced}
            isAdvancedOptions={isAdvancedOptions}
            setIsAdvancedOptions={setIsAdvancedOptions}
            handleSetDefaultProperties={() => handleSetDefaultProperties(currentConfig, -1)}
          />
          {advanced && (
            <SparkLineAdvancedInputs
              currentConfig={currentConfig}
              onDatasetChange={setCurrentConfig}
              onLineConfigChange={(input: string, value: LineConfig) =>
                setCurrentConfig({ ...currentConfig, [input]: value })
              }
              isAdvancedOptions={isAdvancedOptions}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SparkLineEditor;
